import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Loader2 } from "lucide-react";
import { getApiErrorMessage } from "../lib/api";

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    nome: "",
  });

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get("tab") || searchParams.get("mode");

    setIsLogin(!["register", "cadastro", "signup"].includes(String(tab).toLowerCase()));
  }, [location.search]);

  const toggleMode = () => {
    const nextIsLogin = !isLogin;
    setIsLogin(nextIsLogin);
    navigate(nextIsLogin ? "/login" : "/login?tab=register", { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password);
        toast.success("Login realizado com sucesso!");
        
        if (user.tipo === "parceiro") {
          navigate("/parceiro");
        } else {
          navigate("/admin");
        }
      } else {
        const user = await register({
          email: formData.email,
          password: formData.password,
          nome: formData.nome,
          tipo: "admin",
        });
        if (user?.pending_approval) {
          toast.success("Solicitação enviada para aprovação!");
          setIsLogin(true);
          navigate("/login", { replace: true });
          setFormData({ email: formData.email, password: "", nome: "" });
          return;
        }
        toast.success("Conta criada com sucesso!");
        
        if (user.tipo === "parceiro") {
          navigate("/parceiro");
        } else {
          navigate("/admin");
        }
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível processar sua solicitação."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </button>

          <div className="mb-8">
            <div className="mb-5 flex items-center gap-3"><img src="/pin-clube-icon.png" alt="" className="h-16 w-16 rounded-lg" /><span className="font-heading text-2xl font-bold text-[#0B2D66]">PIN Clube</span></div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">
              {isLogin ? "Bem-vindo de volta" : "Solicite seu acesso"}
            </h1>
            <p className="mt-2 text-slate-600">
              {isLogin
                ? "Entre para acessar sua conta"
                : "Preencha seus dados para o administrador master liberar sua conta"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="nome">Nome completo</Label>
                <Input
                  id="nome"
                  type="text"
                  placeholder="Seu nome"
                  value={formData.nome}
                  onChange={(e) =>
                    setFormData({ ...formData, nome: e.target.value })
                  }
                  className="input-field"
                  required={!isLogin}
                  data-testid="nome-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="input-field"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="input-field pr-10"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-12"
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isLogin ? (
                "Entrar"
              ) : (
                "Solicitar acesso"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={toggleMode}
              className="text-primary hover:text-primary/80 font-medium transition-colors"
              data-testid="toggle-mode-btn"
            >
              {isLogin
                ? "Não tem conta? Solicitar acesso"
                : "Já tem conta? Fazer login"}
            </button>
          </div>

          {/* First access note */}
          <div className="mt-8 p-4 bg-blue-50 rounded-xl">
            <p className="text-sm font-medium text-blue-900 mb-2">
              Primeiro acesso:
            </p>
            <p className="text-sm text-blue-700">
              Solicite uma conta administrativa. O acesso fica disponível depois da aprovação do administrador master.
            </p>
            <p className="mt-2 text-sm text-blue-700">
              Parceiros acessam com o email cadastrado pela loja e a senha inicial informada no cadastro.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary to-indigo-600" />
        <img
          src="parceiro_vendas.png"
          alt="Profissional"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40"
        />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="flex flex-col items-center text-center text-white">
            <h2 className="font-heading text-4xl font-bold mb-4">
              Transforme parceiros em vendedores
            </h2>
            <p className="text-xl text-blue-100 max-w-md">
              Programa de incentivos que realmente funciona para o seu negócio.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
